import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import {
  HardHat, ArrowRight, Bell, CalendarCheck, ShieldCheck,
  Building2, FileText, CheckCircle2, MapPin, AlertTriangle, Sparkles, Info,
} from "lucide-react";

const IMG_HERO    = "https://images.unsplash.com/photo-1552505557-c085030eea99?crop=entropy&cs=srgb&fm=jpg&q=85&w=2200";
const IMG_FOREMAN = "https://images.unsplash.com/photo-1503387762-592deb58ef4e?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";
const IMG_DOCS    = "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

const STATS = [
  { value: "30–50", label: "DOB lookups skipped weekly" },
  { value: "<2s",   label: "dashboard load time" },
  { value: "24/7",  label: "permit status monitoring" },
  { value: "0",     label: "missed inspections" },
];

const FEATURES = [
  {
    icon: FileText,
    title: "Manage every permit on one screen",
    body: "Monitor all your DOB filings across all your job sites in a single, sortable view, color-coded by status so you can scan it in seconds.",
  },
  {
    icon: Bell,
    title: "Real-time email alerts",
    body: "The moment a permit gets approved, denied, or flagged as delayed, you get an email alert.",
  },
  {
    icon: CalendarCheck,
    title: "Inspection reminders",
    body: "24h and 48h heads-ups on every scheduled inspection, with calendar and list views built for the field.",
  },
  {
    icon: AlertTriangle,
    title: "Delay flagging",
    body: "We benchmark each permit against the NYC average. Stuck more than 1.5× the norm? You see a yellow flag instantly.",
  },
  {
    icon: MapPin,
    title: "Find permits by address",
    body: "Type the job address and we pull every open DOB filing automatically from NYC Open Data. No manual entry.",
  },
  {
    icon: ShieldCheck,
    title: "DOB NOW synced",
    body: "We monitor NYC's DOB NOW portal continuously so you don't have to. Status changes show in your dashboard in minutes.",
  },
];

const STEPS = [
  { n: "01", title: "Add a job",      body: "Type in the address and we find every open permit on file from NYC's DOB." },
  { n: "02", title: "We monitor",     body: "Background checks every few hours for status changes you'd otherwise miss." },
  { n: "03", title: "You get alerted", body: "Email lands. Permit approved. Inspection in 48h. No more manual lookups." },
];

const PLANS = [
  {
    name: "Free Trial",
    price: 0,
    sub: "Try before you buy",
    isTrial: true,
    features: [
      "No credit card required",
      "Up to 2 active jobs",
      "Basic permit tracking",
      "Email alerts",
    ],
  },
  {
    name: "Starter",
    price: 19,
    sub: "For small GCs",
    features: [
      "Up to 5 active jobs",
      "Up to 20 permits per job",
      "Email alerts",
      "Inspection reminders",
    ],
  },
  {
    name: "Pro",
    price: 39,
    sub: "Most popular",
    popular: true,
    features: [
      "Up to 25 active jobs",
      "Unlimited permits",
      "Email alerts",
      "Delay flagging",
      "Priority polling",
    ],
  },
  {
    name: "Agency",
    price: 89,
    sub: "High-volume tracking",
    features: [
      "Unlimited jobs & permits",
      "Email alerts",
      "Advanced violation tracking",
      "Priority polling",
    ],
  },
];

const FAQ = [
  {
    q: "What is Builders Binder?",
    a: "Builders Binder is a SaaS tool built for New York City contractors, GCs, and project managers. It automatically monitors NYC permit and violation records and sends email alerts whenever something important changes, so you never miss an inspection or approval.",
  },
  {
    q: "How does it work with DOB NOW?",
    a: "We connect to NYC Open Data and the DOB NOW API to poll permit statuses across all your active job sites. When anything changes (approval, denial, inspection scheduled) we detect it and notify you instantly.",
  },
  {
    q: "Do all plans include email alerts?",
    a: "Yes. All plans include email alerts for the permit, filing, inspection, and violation triggers available to that plan.",
  },
  {
    q: "Can I try it before subscribing?",
    a: "Yes. Every account starts with a free trial so you can add jobs, explore the dashboard, and see permit data before committing to a plan.",
  },
  {
    q: "What boroughs are supported?",
    a: "All five NYC boroughs: Manhattan, Brooklyn, Queens, The Bronx, and Staten Island. Any address with a DOB filing is supported.",
  },
];

const COMPARISON_ROWS = [
  { isHeader: true, label: "Jobs & Permits" },
  { label: "Active jobs",              values: ["2 jobs",       "5 jobs",     "25 jobs",      "Unlimited"] },
  { label: "Permits per job",          values: ["Basic",        "Up to 20",   "Unlimited",    "Unlimited"] },
  { label: "Boroughs supported",       values: ["All 5",        "All 5",      "All 5",        "All 5"] },
  { isHeader: true, label: "Alerts & Monitoring" },
  { label: "Email alerts",             values: [true,           true,         true,           true] },
  { label: "Inspection reminders",     values: [false,          true,         true,           true] },
  { label: "Delay flagging",           values: [false,          false,        true,           true] },
  { label: "Polling frequency",        values: ["Standard",     "Standard",   "Standard",     "Priority"] },
  { isHeader: true, label: "Support" },
  { label: "Email support",            values: [true,           true,         true,           true] },
  { label: "Priority support",         values: [false,          false,        false,          true] },
];

// Feature row for plan deep-dive cards
function PlanFeature({ yes, label, detail }) {
  return (
    <div className="flex items-start gap-2.5">
      <span className={`mt-0.5 shrink-0 w-4 h-4 rounded-full flex items-center justify-center ${yes ? "bg-amber-500/20 text-amber-400" : "bg-zinc-800 text-zinc-600"}`}>
        {yes ? (
          <svg width="9" height="9" viewBox="0 0 9 9" fill="none"><path d="M1.5 4.5L3.5 6.5L7.5 2.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
        ) : (
          <svg width="8" height="2" viewBox="0 0 8 2" fill="none"><path d="M1 1H7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
        )}
      </span>
      <div className="min-w-0">
        <span className={`text-sm font-medium leading-tight ${yes ? "text-zinc-100" : "text-zinc-600"}`}>{label}</span>
        {detail && yes && <p className="text-xs text-zinc-500 mt-0.5 leading-snug">{detail}</p>}
      </div>
    </div>
  );
}

// Simple info tooltip that stays within viewport on mobile
function InfoTooltip({ text }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    document.addEventListener("touchstart", close);
    return () => { document.removeEventListener("mousedown", close); document.removeEventListener("touchstart", close); };
  }, [open]);

  return (
    <span ref={ref} className="relative inline-flex items-center" style={{ verticalAlign: "middle" }}>
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        aria-label="More info"
        className="ml-1 text-zinc-400 hover:text-zinc-600 focus:outline-none"
        style={{ lineHeight: 0 }}
      >
        <Info size={13} />
      </button>
      {open && (
        <span
          className="absolute left-0 top-full mt-1.5 z-50 bg-zinc-900 text-white text-xs rounded-sm px-3 py-2 shadow-lg"
          style={{ width: "min(240px, 80vw)", whiteSpace: "normal", lineHeight: "1.4" }}
        >
          {text}
          <span className="absolute -top-1 left-2 w-2 h-2 bg-zinc-900 rotate-45" style={{ display: "block", marginTop: 0 }} />
        </span>
      )}
    </span>
  );
}

export default function Landing() {
  const [authed, setAuthed] = useState(false);
  useEffect(() => { api.get("/auth/me").then(() => setAuthed(true)).catch(() => {}); }, []);

  return (
    <div className="bg-[#FDFDFD] text-zinc-950" data-testid="landing-page">

      {/* NAV */}
      <header className="sticky top-0 z-40 bg-white/90 backdrop-blur border-b border-zinc-200">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-14 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="nav-brand" aria-label="Builders Binder home">
            <div className="w-7 h-7 bg-amber-500 rounded-sm flex items-center justify-center">
              <HardHat size={15} className="text-zinc-950" />
            </div>
            <div>
              <div className="font-display font-bold tracking-tight text-sm leading-none">Builder's Binder</div>
              <div className="label-eyebrow mt-0.5" style={{ fontSize: "9px" }}>Build Smarter</div>
            </div>
          </Link>
          <nav className="hidden md:flex items-center gap-7 text-sm" aria-label="Main navigation">
            <a href="#features" className="text-zinc-600 hover:text-zinc-950">Features</a>
            <a href="#how"      className="text-zinc-600 hover:text-zinc-950">How it works</a>
            <a href="#pricing"  className="text-zinc-600 hover:text-zinc-950">Pricing</a>
            <a href="#faq"      className="text-zinc-600 hover:text-zinc-950">FAQ</a>
          </nav>
          <div className="flex items-center gap-2">
            {authed ? (
              <Link to="/dashboard"
                className="px-3 py-1.5 text-sm font-semibold bg-zinc-950 text-white rounded-sm hover:bg-zinc-800"
                data-testid="nav-dashboard-btn">
                Open Dashboard
              </Link>
            ) : (
              <>
                <Link to="/login"
                  className="hidden sm:inline px-3 py-1.5 text-sm text-zinc-700 hover:text-zinc-950"
                  data-testid="nav-signin">Sign in</Link>
                <Link to="/signup"
                  className="px-3 py-1.5 text-sm font-semibold bg-zinc-950 text-white rounded-sm hover:bg-zinc-800"
                  data-testid="nav-signup">Get started free</Link>
              </>
            )}
          </div>
        </div>
      </header>

      {/* HERO */}
      <section className="relative overflow-hidden" aria-label="Hero">
        <div className="absolute inset-0">
          <img
            src={IMG_HERO}
            alt="NYC construction job site"
            className="w-full h-full object-cover"
            loading="eager"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-zinc-950/95 via-zinc-950/80 to-zinc-950/40" />
        </div>
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 pt-20 pb-28 lg:pt-32 lg:pb-40">
          <div className="max-w-3xl text-white">
            <span className="inline-flex items-center gap-2 px-3 py-1 border border-amber-400/40 bg-amber-500/10 text-amber-300 text-xs font-semibold rounded-sm tracking-wider">
              <Sparkles size={12} /> BUILT FOR NYC CONTRACTORS
            </span>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-7xl font-bold tracking-tight mt-6 leading-[1.02]">
              Manage every DOB permit in one place.
            </h1>
            <p className="mt-6 text-lg sm:text-xl text-zinc-300 max-w-2xl leading-relaxed">
              Stop refreshing DOB NOW. Stop missing inspections. We will help you track every filing across every NYC job site with instant notifications the second anything changes.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Link to="/signup"
                data-testid="hero-cta-signup"
                className="inline-flex items-center justify-center gap-2 bg-amber-500 text-zinc-950 font-semibold px-6 py-3.5 rounded-sm hover:bg-amber-400 transition-colors group">
                Start free trial <ArrowRight size={16} className="group-hover:translate-x-0.5 transition-transform" />
              </Link>
              <Link to="/login"
                data-testid="hero-cta-signin"
                className="inline-flex items-center justify-center gap-2 bg-white/10 backdrop-blur border border-white/20 text-white font-semibold px-6 py-3.5 rounded-sm hover:bg-white/20 transition-colors">
                Sign in
              </Link>
            </div>
            <div className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-6 max-w-2xl">
              {STATS.map(s => (
                <div key={s.label}>
                  <div className="font-display font-bold text-2xl sm:text-3xl text-amber-300">{s.value}</div>
                  <div className="text-xs text-zinc-400 mt-1 uppercase tracking-wider">{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-12 bg-gradient-to-t from-[#FDFDFD] to-transparent pointer-events-none" />
      </section>

      {/* PROBLEM */}
      <section className="py-16 sm:py-24 border-b border-zinc-200" aria-label="The problem">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <span className="label-eyebrow">The Problem</span>
            <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-3 leading-tight">
              10 active jobs. 50 manual DOB NOW lookups a week.
            </h2>
            <p className="mt-5 text-zinc-600 leading-relaxed">
              You're logging in, searching permit numbers one by one, copy-pasting status changes into a spreadsheet.
              Inspections get missed. Denials blindside you. The city office doesn't pick up.
            </p>
            <p className="mt-4 text-zinc-600 leading-relaxed">
              <strong className="text-zinc-950">Every minute spent refreshing DOB NOW is a minute not spent in the field.</strong>
            </p>
            <ul className="mt-6 space-y-2.5">
              {[
                "Manual lookups for every permit, every week",
                "Missed inspection appointments cost real money",
                "Permit denials surprise you 3 days late",
                "No single view across all active jobs",
              ].map(p => (
                <li key={p} className="flex items-start gap-2 text-sm">
                  <span className="mt-1.5 w-1 h-1 bg-red-500 rounded-full flex-shrink-0" /> {p}
                </li>
              ))}
            </ul>
          </div>
          <div className="relative">
            <img
              src={IMG_FOREMAN}
              alt="NYC contractor reviewing permit status on a job site"
              className="w-full h-[420px] object-cover rounded-sm"
              loading="lazy"
            />
            <div className="absolute -bottom-4 -left-4 bg-white border border-zinc-200 rounded-sm p-4 shadow-lg max-w-xs hidden md:block">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-green-500 rounded-full" />
                <span className="label-eyebrow">Live alert</span>
              </div>
              <p className="text-sm font-medium mt-2">
                Permit #EL98234 just changed from{" "}
                <span className="status-pill status-warning ml-1">Pending</span> to{" "}
                <span className="status-pill status-success ml-1">Approved</span>
              </p>
              <p className="text-xs text-zinc-500 mt-1">45 Court St — 2 minutes ago</p>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section id="features" className="py-16 sm:py-24 border-b border-zinc-200 bg-white" aria-label="Features">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="max-w-2xl">
            <span className="label-eyebrow">What you get</span>
            <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-3 leading-tight">
              Built for the way NYC contractors actually work.
            </h2>
            <p className="mt-4 text-zinc-600">
              Real-time DOB permit tracking, inspection reminders, and instant alerts, all in one dashboard.
            </p>
          </div>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {FEATURES.map(({ icon: Icon, title, body }) => (
              <article key={title} className="p-6 border border-zinc-200 rounded-sm bg-white hover:border-zinc-400 transition-colors">
                <div className="w-9 h-9 bg-zinc-950 text-amber-400 rounded-sm flex items-center justify-center">
                  <Icon size={18} strokeWidth={1.75} aria-hidden="true" />
                </div>
                <h3 className="font-display font-bold text-base tracking-tight mt-4">{title}</h3>
                <p className="text-sm text-zinc-600 mt-1.5 leading-relaxed">{body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how" className="py-16 sm:py-24 border-b border-zinc-200 bg-zinc-950 text-white relative overflow-hidden" aria-label="How it works">
        <div className="absolute inset-0 opacity-20">
          <img src={IMG_DOCS} alt="" className="w-full h-full object-cover" aria-hidden="true" loading="lazy" />
        </div>
        <div className="absolute inset-0 bg-zinc-950/85" />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
          <span className="label-eyebrow text-amber-400">How it works</span>
          <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-3 leading-tight max-w-2xl">
            Three steps. Less than two minutes to set up.
          </h2>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
            {STEPS.map((s, i) => (
              <div key={s.n} className="relative">
                <div className="font-display text-6xl font-bold text-amber-400/30" aria-hidden="true">{s.n}</div>
                <h3 className="font-display font-bold text-xl tracking-tight mt-2">{s.title}</h3>
                <p className="text-sm text-zinc-400 mt-2 leading-relaxed">{s.body}</p>
                {i < STEPS.length - 1 && (
                  <div className="hidden md:block absolute top-8 right-0 translate-x-3 text-amber-400/40" aria-hidden="true">
                    <ArrowRight size={20} />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing" className="py-16 sm:py-24 border-b border-zinc-200 bg-white" aria-label="Pricing">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="text-center max-w-2xl mx-auto">
            <span className="label-eyebrow">Pricing</span>
            <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-3 leading-tight">
              Plans that pay for themselves in one saved inspection.
            </h2>
            <p className="mt-3 text-sm text-zinc-500">Start free or pick a plan. No credit card required for the free trial.</p>
          </div>

          {/* Plan cards */}
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 max-w-6xl mx-auto">
            {PLANS.map(p => {
              const popular = p.popular;
              const isTrial = p.isTrial;
              return (
                <div
                  key={p.name}
                  className={`relative p-6 border rounded-sm bg-white flex flex-col ${
                    popular ? "border-zinc-950 ring-1 ring-zinc-950" :
                    isTrial ? "border-green-400 ring-1 ring-green-400" :
                    "border-zinc-200"
                  }`}
                >
                  {popular && (
                    <div className="absolute -top-2 left-5 bg-amber-500 text-zinc-950 text-[10px] font-bold tracking-wider px-2 py-0.5 rounded-sm">
                      MOST POPULAR
                    </div>
                  )}
                  {isTrial && (
                    <div className="absolute -top-2 left-5 bg-green-500 text-white text-[10px] font-bold tracking-wider px-2 py-0.5 rounded-sm">
                      FREE TO START
                    </div>
                  )}
                  <div className="label-eyebrow">{p.name}</div>
                  <div className="text-xs text-zinc-500 mt-0.5">{p.sub}</div>
                  <div className="font-display text-4xl font-bold tracking-tight mt-4">
                    {isTrial ? (
                      <span className="text-green-600">$0</span>
                    ) : (
                      <>${p.price}</>
                    )}
                    <span className="text-sm text-zinc-500 font-normal">
                      {isTrial ? " free" : "/mo"}
                    </span>
                  </div>
                  <ul className="mt-5 space-y-2 flex-1">
                    {p.features.map(f => (
                      <li key={f} className="text-sm flex items-start gap-2">
                        <CheckCircle2 size={14} className={`mt-0.5 flex-shrink-0 ${isTrial ? "text-green-500" : "text-green-600"}`} aria-hidden="true" /> {f}
                      </li>
                    ))}
                  </ul>
                  <Link
                    to="/signup"
                    className={`mt-6 block text-center py-2.5 rounded-sm text-sm font-semibold transition-colors ${
                      popular ? "bg-zinc-950 text-white hover:bg-zinc-800" :
                      isTrial ? "bg-green-500 text-white hover:bg-green-600" :
                      "border border-zinc-200 hover:bg-zinc-50"
                    }`}
                  >
                    {isTrial ? "Start for free" : `Subscribe — $${p.price}/mo`}
                  </Link>
                </div>
              );
            })}
          </div>

          {/* Side-by-side comparison table */}
          <div className="mt-16 max-w-6xl mx-auto">
            <h3 className="font-display text-xl font-bold tracking-tight text-center mb-8">Compare plans side by side</h3>
            <div className="overflow-x-auto rounded-sm border border-zinc-200" style={{ overflowY: "visible" }}>
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="border-b border-zinc-200">
                    <th className="text-left px-5 py-4 font-semibold text-zinc-500 w-2/5 bg-zinc-50">Feature</th>
                    {PLANS.map(p => (
                      <th key={p.name} className={`px-4 py-4 text-center font-bold tracking-tight ${p.popular ? "bg-zinc-950 text-white" : p.isTrial ? "bg-green-50 text-green-800" : "bg-zinc-50 text-zinc-900"}`}>
                        <div className="font-display">{p.name}</div>
                        <div className={`text-base font-bold mt-1 ${p.popular ? "text-amber-400" : p.isTrial ? "text-green-600" : "text-zinc-700"}`}>
                          {p.isTrial ? "$0" : `$${p.price}`}
                          <span className={`text-xs font-normal ml-0.5 ${p.popular ? "text-zinc-400" : "text-zinc-400"}`}>{p.isTrial ? "" : "/mo"}</span>
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {COMPARISON_ROWS.map((row, i) => (
                    row.isHeader ? (
                      <tr key={row.label} className="bg-zinc-100 border-t border-b border-zinc-200">
                        <td colSpan={5} className="px-5 py-2.5 text-xs font-bold uppercase tracking-widest text-zinc-500">{row.label}</td>
                      </tr>
                    ) : (
                      <tr key={row.label} className={`border-t border-zinc-100 ${i % 2 === 0 ? "bg-white" : "bg-zinc-50/50"}`}>
                        <td className="px-5 py-3.5 text-zinc-700 font-medium">
                          <span className="inline-flex items-center gap-1">
                            {row.label}
                            {row.tooltip && <InfoTooltip text={row.tooltip} />}
                          </span>
                        </td>
                        {row.values.map((val, vi) => (
                          <td key={vi} className={`px-4 py-3.5 text-center ${PLANS[vi]?.popular ? "bg-zinc-950/5" : ""}`}>
                            {val === true ? (
                              <CheckCircle2 size={16} className="text-green-500 mx-auto" />
                            ) : val === false ? (
                              <span className="text-zinc-300 text-lg leading-none">—</span>
                            ) : (
                              <span className="text-zinc-700 font-medium">{val}</span>
                            )}
                          </td>
                        ))}
                      </tr>
                    )
                  ))}
                  {/* CTA row */}
                  <tr className="border-t border-zinc-200 bg-white">
                    <td className="px-5 py-4" />
                    {PLANS.map(p => (
                      <td key={p.name} className={`px-4 py-4 text-center ${p.popular ? "bg-zinc-950/5" : ""}`}>
                        <Link
                          to="/signup"
                          className={`inline-block px-4 py-2 rounded-sm text-xs font-bold transition-colors ${
                            p.popular ? "bg-zinc-950 text-white hover:bg-zinc-800" :
                            p.isTrial ? "bg-green-500 text-white hover:bg-green-600" :
                            "border border-zinc-300 text-zinc-700 hover:bg-zinc-50"
                          }`}
                        >
                          {p.isTrial ? "Start free" : "Get started"}
                        </Link>
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <p className="text-center text-xs text-zinc-400 mt-6">All plans billed monthly. Cancel anytime. Payments secured by Paddle.</p>
        </div>
      </section>

      {/* PLAN DEEP-DIVE */}
      <section id="plan-compare" className="py-16 sm:py-24 border-b border-zinc-200 bg-zinc-950 text-white" aria-label="Plan comparison">
        <div className="max-w-6xl mx-auto px-5 sm:px-8">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <span className="label-eyebrow" style={{ color: "#a1a1aa" }}>Choose your plan</span>
            <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-3 leading-tight text-white">
              What's included in each plan
            </h2>
            <p className="mt-3 text-sm text-zinc-400">Every paid plan starts from the free trial. Upgrade, downgrade, or cancel at any time.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {/* ── STARTER ── */}
            <div className="rounded-sm border border-zinc-800 bg-zinc-900 flex flex-col">
              <div className="p-6 border-b border-zinc-800">
                <div className="label-eyebrow" style={{ color: "#a1a1aa" }}>Starter</div>
                <div className="font-display text-4xl font-bold tracking-tight mt-3 text-white">
                  $19<span className="text-base font-normal text-zinc-500">/mo</span>
                </div>
                <p className="text-sm text-zinc-400 mt-2">For solo GCs and small contractors managing a handful of active sites.</p>
                <Link to="/signup" className="mt-5 flex items-center justify-center gap-2 w-full py-2.5 border border-zinc-700 text-white text-sm font-semibold rounded-sm hover:bg-zinc-800 transition-colors">
                  Get started
                </Link>
              </div>
              <div className="p-6 flex flex-col gap-3 flex-1">
                <PlanFeature yes label="5 active jobs" detail="Track up to 5 job sites simultaneously" />
                <PlanFeature yes label="Up to 20 permits per job" detail="Covers most standard GC workloads" />
                <PlanFeature yes label="All 5 NYC boroughs" detail="Manhattan, Brooklyn, Queens, Bronx, Staten Island" />
                <PlanFeature yes label="Email alerts" detail="Instant notification on every permit status change" />
                <PlanFeature yes label="Inspection reminders" detail="24h and 48h advance notice before scheduled inspections" />
                <PlanFeature yes label="Standard polling" detail="Status checks every few hours" />
                <PlanFeature yes label="Email support" />
                <div className="my-1 border-t border-zinc-800" />
                <PlanFeature no label="Delay flagging" />
                <PlanFeature no label="Priority polling" />
                <PlanFeature no label="Priority support" />
              </div>
            </div>

            {/* ── PRO ── */}
            <div className="rounded-sm border border-amber-500 bg-zinc-900 flex flex-col relative">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                <span className="bg-amber-500 text-zinc-950 text-[10px] font-bold tracking-wider px-3 py-1 rounded-sm whitespace-nowrap">
                  MOST POPULAR
                </span>
              </div>
              <div className="p-6 border-b border-zinc-800">
                <div className="label-eyebrow text-amber-400">Pro</div>
                <div className="font-display text-4xl font-bold tracking-tight mt-3 text-white">
                  $39<span className="text-base font-normal text-zinc-500">/mo</span>
                </div>
                <p className="text-sm text-zinc-400 mt-2">For growing GCs who need unlimited permit coverage and stronger monitoring.</p>
                <Link to="/signup" className="mt-5 flex items-center justify-center gap-2 w-full py-2.5 bg-amber-500 text-zinc-950 text-sm font-bold rounded-sm hover:bg-amber-400 transition-colors">
                  Get started
                </Link>
              </div>
              <div className="p-6 flex flex-col gap-3 flex-1">
                <PlanFeature yes label="25 active jobs" detail="Scale across all your concurrent projects" />
                <PlanFeature yes label="Unlimited permits per job" detail="No cap — track every filing on every site" />
                <PlanFeature yes label="All 5 NYC boroughs" detail="Manhattan, Brooklyn, Queens, Bronx, Staten Island" />
                <PlanFeature yes label="Email alerts" detail="Instant notification on every permit status change" />
                <PlanFeature yes label="Inspection reminders" detail="24h and 48h advance notice before scheduled inspections" />
                <PlanFeature yes label="Delay flagging" detail="Flag permits stuck 1.5× longer than the NYC average" />
                <PlanFeature yes label="Standard polling" detail="Status checks every few hours" />
                <PlanFeature yes label="Email support" />
                <div className="my-1 border-t border-zinc-800" />
                <PlanFeature no label="Priority polling" />
                <PlanFeature no label="Priority support" />
              </div>
            </div>

            {/* ── AGENCY ── */}
            <div className="rounded-sm border border-zinc-800 bg-zinc-900 flex flex-col">
              <div className="p-6 border-b border-zinc-800">
                <div className="label-eyebrow" style={{ color: "#a1a1aa" }}>Agency</div>
                <div className="font-display text-4xl font-bold tracking-tight mt-3 text-white">
                  $89<span className="text-base font-normal text-zinc-500">/mo</span>
                </div>
                <p className="text-sm text-zinc-400 mt-2">For high-volume contractors and agencies who need full scale and faster monitoring.</p>
                <Link to="/signup" className="mt-5 flex items-center justify-center gap-2 w-full py-2.5 border border-zinc-700 text-white text-sm font-semibold rounded-sm hover:bg-zinc-800 transition-colors">
                  Get started
                </Link>
              </div>
              <div className="p-6 flex flex-col gap-3 flex-1">
                <PlanFeature yes label="Unlimited jobs" detail="No ceiling — manage your entire portfolio" />
                <PlanFeature yes label="Unlimited permits per job" detail="Every filing tracked, no exceptions" />
                <PlanFeature yes label="All 5 NYC boroughs" detail="Manhattan, Brooklyn, Queens, Bronx, Staten Island" />
                <PlanFeature yes label="Email alerts" detail="Instant notification on every important status change" />
                <PlanFeature yes label="Inspection reminders" detail="24h and 48h advance notice before scheduled inspections" />
                <PlanFeature yes label="Delay flagging" detail="Flag permits stuck 1.5× longer than the NYC average" />
                <PlanFeature yes label="Priority polling" detail="First in line for status checks — faster updates" />
                <PlanFeature yes label="Priority support" detail="Jump to the front of the support queue" />
              </div>
            </div>
          </div>

          <p className="text-center text-xs text-zinc-600 mt-10">
            Not sure which plan fits? <a href="#faq" className="underline underline-offset-2 text-zinc-400 hover:text-white">Read the FAQ</a> or <Link to="/signup" className="underline underline-offset-2 text-zinc-400 hover:text-white">start free</Link> and upgrade any time.
          </p>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-16 sm:py-24 border-b border-zinc-200 bg-[#FDFDFD]" aria-label="Frequently asked questions">
        <div className="max-w-3xl mx-auto px-5 sm:px-8">
          <div className="text-center mb-12">
            <span className="label-eyebrow">FAQ</span>
            <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-3 leading-tight">
              Common questions
            </h2>
          </div>
          <dl className="space-y-6">
            {FAQ.map(({ q, a }) => (
              <div key={q} className="border border-zinc-200 rounded-sm p-5 bg-white">
                <dt className="font-display font-bold text-base tracking-tight">{q}</dt>
                <dd className="mt-2 text-sm text-zinc-600 leading-relaxed">{a}</dd>
              </div>
            ))}
          </dl>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-zinc-950 text-white" aria-label="Call to action">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 text-center">
          <h2 className="font-display text-3xl sm:text-5xl font-bold tracking-tight leading-tight">
            Walk into Monday already knowing.
          </h2>
          <p className="mt-5 text-zinc-400 max-w-xl mx-auto">
            Join NYC contractors and project managers who stopped chasing permits and started shipping jobs on time.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/signup"
              data-testid="footer-cta-signup"
              className="inline-flex items-center justify-center gap-2 bg-amber-500 text-zinc-950 font-semibold px-6 py-3.5 rounded-sm hover:bg-amber-400 transition-colors">
              Start your free trial <ArrowRight size={16} />
            </Link>
            <Link to="/login"
              className="inline-flex items-center justify-center gap-2 bg-white/10 border border-white/20 text-white px-6 py-3.5 rounded-sm hover:bg-white/20 transition-colors">
              Sign in
            </Link>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="py-10 border-t border-zinc-200 bg-white" role="contentinfo">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 flex flex-col md:flex-row md:items-center justify-between gap-4 text-sm text-zinc-500">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 bg-amber-500 rounded-sm flex items-center justify-center">
              <HardHat size={13} className="text-zinc-950" aria-hidden="true" />
            </div>
            <span>Builders Binder — © {new Date().getFullYear()} All rights reserved.</span>
          </div>
          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            <a
              href="mailto:support@buildersbinder.com"
              className="hover:text-zinc-950 underline underline-offset-2"
            >
              support@buildersbinder.com
            </a>
            <nav className="flex gap-5" aria-label="Footer navigation">
              <a href="#features" className="hover:text-zinc-950">Features</a>
              <a href="#pricing"  className="hover:text-zinc-950">Pricing</a>
              <a href="#faq"      className="hover:text-zinc-950">FAQ</a>
              <Link to="/privacy" className="hover:text-zinc-950">Privacy</Link>
              <Link to="/terms"   className="hover:text-zinc-950">Terms</Link>
              <Link to="/login"   className="hover:text-zinc-950">Sign in</Link>
              <Link to="/signup"  className="hover:text-zinc-950">Sign up</Link>
            </nav>
          </div>
        </div>
      </footer>
    </div>
  );
}
